import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  LogOut, Settings, Building2, Upload, Trash2, Image, Save, Loader2, ChevronLeft, CheckCircle, Map
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminCMS() {
  const { t, lang } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Redirect non-super_admin
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  if (!user || user.role !== 'super_admin') return null;

  return (
    <div data-testid="super-admin-cms" className="min-h-screen bg-[#F5F5F7] pt-24 pb-16">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1
              className="text-3xl md:text-4xl font-light tracking-tight mb-1"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A1A1A' }}
              data-testid="cms-title"
            >
              {t('cms.title')}
            </h1>
            <p className="text-sm text-[#8A828F]">
              Super Admin — {user?.name}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              data-testid="cms-to-dashboard-btn"
              className="flex items-center gap-2 text-[#8A828F] hover:text-[#D4AF37] transition-colors text-sm tracking-[0.1em] uppercase"
            >
              <Building2 size={16} />
              Dashboard
            </button>
            <button
              onClick={async () => { await logout(); navigate('/admin'); }}
              data-testid="cms-logout-btn"
              className="flex items-center gap-2 text-[#8A828F] hover:text-[#C62828] transition-colors text-sm tracking-[0.1em] uppercase"
            >
              <LogOut size={16} />
              {t('admin.logout')}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="settings" className="space-y-6">
          <TabsList className="bg-white border border-[#E5E5E5] p-1 h-auto gap-1">
            <TabsTrigger
              value="settings"
              data-testid="tab-settings"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white px-6 py-2.5 text-xs tracking-[0.1em] uppercase rounded-none"
            >
              <Settings size={14} className="mr-2" />
              {t('cms.siteSettings')}
            </TabsTrigger>
            <TabsTrigger
              value="apartments"
              data-testid="tab-apartments"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white px-6 py-2.5 text-xs tracking-[0.1em] uppercase rounded-none"
            >
              <Building2 size={14} className="mr-2" />
              {t('cms.apartments')}
            </TabsTrigger>
            <TabsTrigger
              value="floorplans"
              data-testid="tab-floorplans"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white px-6 py-2.5 text-xs tracking-[0.1em] uppercase rounded-none"
            >
              <Map size={14} className="mr-2" />
              {lang === 'es' ? 'Planos de Planta' : 'Floor Plans'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings">
            <SiteSettingsPanel t={t} lang={lang} />
          </TabsContent>

          <TabsContent value="apartments">
            <ApartmentCMSPanel t={t} lang={lang} />
          </TabsContent>

          <TabsContent value="floorplans">
            <FloorPlansCMSPanel t={t} lang={lang} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ============= SITE SETTINGS PANEL ============= */
function SiteSettingsPanel({ t, lang }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { id, ...data } = settings;
      await axios.put(`${API}/site-settings`, data);
      toast.success(t('cms.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#D4AF37]" size={24} /></div>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Hero Section Settings */}
      <div className="bg-white border border-[#E5E5E5] p-8" data-testid="settings-hero-section">
        <h3 className="text-lg font-light tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Hero Section
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SettingsField label={`${t('cms.heroOverline')} (ES)`} field="hero_overline_es" value={settings.hero_overline_es} onChange={handleChange} testid="hero-overline-es" />
          <SettingsField label={`${t('cms.heroOverline')} (EN)`} field="hero_overline_en" value={settings.hero_overline_en} onChange={handleChange} testid="hero-overline-en" />
          <SettingsField label={t('cms.heroTitle')} field="hero_title" value={settings.hero_title} onChange={handleChange} testid="hero-title" />
          <div /> {/* spacer */}
          <SettingsField label={`${t('cms.heroSubtitle')} (ES)`} field="hero_subtitle_es" value={settings.hero_subtitle_es} onChange={handleChange} textarea testid="hero-subtitle-es" />
          <SettingsField label={`${t('cms.heroSubtitle')} (EN)`} field="hero_subtitle_en" value={settings.hero_subtitle_en} onChange={handleChange} textarea testid="hero-subtitle-en" />
          <SettingsField label={`${t('cms.heroCta')} (ES)`} field="hero_cta_es" value={settings.hero_cta_es} onChange={handleChange} testid="hero-cta-es" />
          <SettingsField label={`${t('cms.heroCta')} (EN)`} field="hero_cta_en" value={settings.hero_cta_en} onChange={handleChange} testid="hero-cta-en" />
        </div>
      </div>

      {/* Contact/Project Info */}
      <div className="bg-white border border-[#E5E5E5] p-8" data-testid="settings-contact-section">
        <h3 className="text-lg font-light tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {t('cms.projectName')} & {lang === 'es' ? 'Contacto' : 'Contact'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SettingsField label={t('cms.projectName')} field="project_name" value={settings.project_name} onChange={handleChange} testid="project-name" />
          <SettingsField label={t('cms.contactPhone')} field="contact_phone" value={settings.contact_phone} onChange={handleChange} testid="contact-phone" />
          <SettingsField label={t('cms.contactEmail')} field="contact_email" value={settings.contact_email} onChange={handleChange} testid="contact-email" />
          <SettingsField label={t('cms.contactAddress')} field="contact_address" value={settings.contact_address} onChange={handleChange} testid="contact-address" />
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-settings-btn"
          className="flex items-center gap-2 bg-[#D4AF37] text-white px-8 py-3.5 uppercase tracking-[0.12em] text-sm font-light hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? t('cms.saving') : t('cms.saveSettings')}
        </button>
      </div>
    </motion.div>
  );
}

function SettingsField({ label, field, value, onChange, textarea, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">{label}</Label>
      {textarea ? (
        <Textarea
          data-testid={testid}
          value={value || ''}
          onChange={e => onChange(field, e.target.value)}
          rows={3}
          className="border-[#E5E5E5] rounded-none focus:border-[#D4AF37] focus-visible:ring-0"
        />
      ) : (
        <Input
          data-testid={testid}
          value={value || ''}
          onChange={e => onChange(field, e.target.value)}
          className="border-[#E5E5E5] rounded-none focus:border-[#D4AF37] focus-visible:ring-0"
        />
      )}
    </div>
  );
}

/* ============= APARTMENT CMS PANEL ============= */
function ApartmentCMSPanel({ t, lang }) {
  const [apartments, setApartments] = useState([]);
  const [selectedApt, setSelectedApt] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingFloorplan, setUploadingFloorplan] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [cmsTower, setCmsTower] = useState('kirei1');
  const floorplanRef = useRef(null);
  const galleryRef = useRef(null);

  const fetchApartments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/apartments?tower=${cmsTower}`);
      setApartments(res.data);
    } catch {}
  }, [cmsTower]);

  useEffect(() => {
    fetchApartments();
    setSelectedApt(null);
  }, [fetchApartments]);

  const selectApartment = (apt) => {
    setSelectedApt(apt);
    setEditData({
      status: apt.status,
      price: apt.price,
      area_m2: apt.area_m2,
      bedrooms: apt.bedrooms,
      bathrooms: apt.bathrooms,
      has_balcony: apt.has_balcony,
      orientation: apt.orientation,
      description: apt.description || '',
    });
  };

  const handleSave = async () => {
    if (!selectedApt) return;
    setSaving(true);
    try {
      await axios.put(`${API}/apartments/${selectedApt.id}`, editData);
      toast.success(lang === 'es' ? 'Apartamento actualizado' : 'Apartment updated');
      const res = await axios.get(`${API}/apartments/${selectedApt.id}`);
      setSelectedApt(res.data);
      fetchApartments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleFloorplanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedApt) return;
    setUploadingFloorplan(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/apartments/${selectedApt.id}/floorplan`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSelectedApt(res.data);
      fetchApartments();
      toast.success(t('cms.uploaded'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFloorplan(false);
      if (floorplanRef.current) floorplanRef.current.value = '';
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedApt) return;
    setUploadingGallery(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/apartments/${selectedApt.id}/gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSelectedApt(res.data);
      fetchApartments();
      toast.success(t('cms.uploaded'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingGallery(false);
      if (galleryRef.current) galleryRef.current.value = '';
    }
  };

  const handleRemoveGalleryImage = async (imagePath) => {
    if (!selectedApt) return;
    try {
      const res = await axios.delete(`${API}/apartments/${selectedApt.id}/gallery`, {
        params: { image_path: imagePath }
      });
      setSelectedApt(res.data);
      fetchApartments();
      toast.success(lang === 'es' ? 'Imagen eliminada' : 'Image removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  // Group apartments by floor
  const floors = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Left: Apartment list */}
      <div className="lg:col-span-4 bg-white border border-[#E5E5E5] overflow-hidden" data-testid="apt-list-panel">
        <div className="p-5 border-b border-[#E5E5E5]">
          <h3 className="text-lg font-light tracking-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t('cms.apartments')}
          </h3>
          <div className="flex gap-2">
            {['kirei1', 'kirei2'].map(tw => (
              <button
                key={tw}
                data-testid={`cms-tower-${tw}`}
                onClick={() => setCmsTower(tw)}
                className={`px-4 py-1.5 text-xs tracking-[0.1em] uppercase border transition-colors ${
                  cmsTower === tw
                    ? 'bg-[#D4AF37] text-white border-[#D4AF37]'
                    : 'text-[#8A828F] border-[#E5E5E5] hover:border-[#D4AF37]'
                }`}
              >
                {tw === 'kirei1' ? 'Kirei I' : 'Kirei II'}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {floors.map(floor => {
            const floorApts = apartments.filter(a => a.floor === floor);
            return (
              <div key={floor}>
                <div className="px-5 py-2 bg-[#F5F5F7] border-b border-[#E5E5E5]">
                  <span className="text-[10px] tracking-[0.15em] uppercase text-[#8A828F]">
                    {floor >= 9 ? 'PH — ' : ''}{t('admin.floor')} {floor}
                  </span>
                </div>
                {floorApts.map(apt => (
                  <button
                    key={apt.id}
                    data-testid={`cms-apt-${apt.id}`}
                    onClick={() => selectApartment(apt)}
                    className={`w-full flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5] text-left transition-colors ${
                      selectedApt?.id === apt.id ? 'bg-[#D4AF37]/10 border-l-2 border-l-[#D4AF37]' : 'hover:bg-[#F5F5F7]'
                    }`}
                  >
                    <div>
                      <span className="text-sm font-medium text-[#1A1A1A]">{apt.id}</span>
                      <span className="text-xs text-[#8A828F] ml-2">{apt.area_m2}m² — ${apt.price.toLocaleString()}</span>
                    </div>
                    <StatusDot status={apt.status} />
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: Edit panel */}
      <div className="lg:col-span-8" data-testid="apt-edit-panel">
        {selectedApt ? (
          <div className="space-y-6">
            {/* Details card */}
            <div className="bg-white border border-[#E5E5E5] p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {t('cms.editApartment')} — {selectedApt.id}
                  </h3>
                  <p className="text-xs text-[#8A828F] mt-1">{t('admin.floor')} {selectedApt.floor}, {t('admin.unit')} {selectedApt.unit}</p>
                </div>
                <button
                  onClick={() => setSelectedApt(null)}
                  className="text-xs text-[#8A828F] hover:text-[#1A1A1A] border border-[#E5E5E5] px-3 py-1.5"
                >
                  {t('cms.back')}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Status */}
                <div>
                  <Label className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">{t('admin.status')}</Label>
                  <Select value={editData.status} onValueChange={v => setEditData(p => ({ ...p, status: v }))}>
                    <SelectTrigger className="border-[#E5E5E5] rounded-none" data-testid="cms-edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">{lang === 'es' ? 'Disponible' : 'Available'}</SelectItem>
                      <SelectItem value="reserved">{lang === 'es' ? 'Reservado' : 'Reserved'}</SelectItem>
                      <SelectItem value="sold">{lang === 'es' ? 'Vendido' : 'Sold'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Price */}
                <div>
                  <Label className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">{t('admin.price')} (USD)</Label>
                  <Input
                    type="number"
                    data-testid="cms-edit-price"
                    value={editData.price || ''}
                    onChange={e => setEditData(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
                    className="border-[#E5E5E5] rounded-none focus:border-[#D4AF37] focus-visible:ring-0"
                  />
                </div>

                {/* Area */}
                <div>
                  <Label className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">{t('admin.area')} (m²)</Label>
                  <Input
                    type="number"
                    data-testid="cms-edit-area"
                    value={editData.area_m2 || ''}
                    onChange={e => setEditData(p => ({ ...p, area_m2: parseFloat(e.target.value) || 0 }))}
                    className="border-[#E5E5E5] rounded-none focus:border-[#D4AF37] focus-visible:ring-0"
                  />
                </div>

                {/* Bedrooms */}
                <div>
                  <Label className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">{t('floorplan.bedrooms')}</Label>
                  <Input
                    type="number"
                    data-testid="cms-edit-bedrooms"
                    value={editData.bedrooms || ''}
                    onChange={e => setEditData(p => ({ ...p, bedrooms: parseInt(e.target.value) || 0 }))}
                    className="border-[#E5E5E5] rounded-none focus:border-[#D4AF37] focus-visible:ring-0"
                  />
                </div>

                {/* Bathrooms */}
                <div>
                  <Label className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">{t('floorplan.bathrooms')}</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    data-testid="cms-edit-bathrooms"
                    value={editData.bathrooms || ''}
                    onChange={e => setEditData(p => ({ ...p, bathrooms: parseFloat(e.target.value) || 0 }))}
                    className="border-[#E5E5E5] rounded-none focus:border-[#D4AF37] focus-visible:ring-0"
                  />
                </div>

                {/* Balcony */}
                <div>
                  <Label className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">{t('floorplan.balcony')}</Label>
                  <Select value={editData.has_balcony ? 'yes' : 'no'} onValueChange={v => setEditData(p => ({ ...p, has_balcony: v === 'yes' }))}>
                    <SelectTrigger className="border-[#E5E5E5] rounded-none" data-testid="cms-edit-balcony">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">{t('floorplan.yes')}</SelectItem>
                      <SelectItem value="no">{t('floorplan.no')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Orientation */}
                <div>
                  <Label className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">{t('floorplan.orientation')}</Label>
                  <Input
                    data-testid="cms-edit-orientation"
                    value={editData.orientation || ''}
                    onChange={e => setEditData(p => ({ ...p, orientation: e.target.value }))}
                    className="border-[#E5E5E5] rounded-none focus:border-[#D4AF37] focus-visible:ring-0"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <Label className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">{t('cms.description')}</Label>
                  <Textarea
                    data-testid="cms-edit-description"
                    value={editData.description || ''}
                    onChange={e => setEditData(p => ({ ...p, description: e.target.value }))}
                    rows={3}
                    className="border-[#E5E5E5] rounded-none focus:border-[#D4AF37] focus-visible:ring-0"
                  />
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="cms-save-apt-btn"
                  className="flex items-center gap-2 bg-[#D4AF37] text-white px-8 py-3 uppercase tracking-[0.12em] text-sm font-light hover:bg-[#B5952F] transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {t('admin.save')}
                </button>
              </div>
            </div>

            {/* Floorplan upload */}
            <div className="bg-white border border-[#E5E5E5] p-8" data-testid="floorplan-upload-section">
              <h3 className="text-lg font-light tracking-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {t('cms.floorplanImage')}
              </h3>
              {selectedApt.floorplan_image && !selectedApt.floorplan_image.startsWith('/floorplan/') && (
                <div className="mb-4">
                  <p className="text-xs text-[#8A828F] mb-2">{t('cms.currentFloorplan')}:</p>
                  <StorageImage path={selectedApt.floorplan_image} alt="Floorplan" className="max-h-48 border border-[#E5E5E5]" />
                </div>
              )}
              <input
                type="file"
                ref={floorplanRef}
                accept="image/*,.svg,.pdf"
                onChange={handleFloorplanUpload}
                className="hidden"
                data-testid="floorplan-file-input"
              />
              <button
                onClick={() => floorplanRef.current?.click()}
                disabled={uploadingFloorplan}
                data-testid="upload-floorplan-btn"
                className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-[#D4AF37] hover:text-white transition-all disabled:opacity-50"
              >
                {uploadingFloorplan ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploadingFloorplan ? t('cms.uploading') : t('cms.uploadFloorplan')}
              </button>
            </div>

            {/* Gallery */}
            <div className="bg-white border border-[#E5E5E5] p-8" data-testid="gallery-upload-section">
              <h3 className="text-lg font-light tracking-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {t('cms.galleryImages')}
              </h3>
              {selectedApt.gallery_images && selectedApt.gallery_images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  {selectedApt.gallery_images.map((img, idx) => (
                    <div key={idx} className="relative group border border-[#E5E5E5] overflow-hidden">
                      <StorageImage path={img} alt={`Gallery ${idx + 1}`} className="w-full h-32 object-cover" />
                      <button
                        onClick={() => handleRemoveGalleryImage(img)}
                        data-testid={`remove-gallery-${idx}`}
                        className="absolute top-2 right-2 bg-red-600 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#8A828F] mb-4">{t('cms.noGalleryImages')}</p>
              )}
              <input
                type="file"
                ref={galleryRef}
                accept="image/*"
                onChange={handleGalleryUpload}
                className="hidden"
                data-testid="gallery-file-input"
              />
              <button
                onClick={() => galleryRef.current?.click()}
                disabled={uploadingGallery}
                data-testid="upload-gallery-btn"
                className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-[#D4AF37] hover:text-white transition-all disabled:opacity-50"
              >
                {uploadingGallery ? <Loader2 size={14} className="animate-spin" /> : <Image size={14} />}
                {uploadingGallery ? t('cms.uploading') : t('cms.uploadPhoto')}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-dashed border-[#E5E5E5] p-16 flex flex-col items-center justify-center text-center">
            <Building2 size={48} className="text-[#E5E5E5] mb-4" />
            <p className="text-sm text-[#8A828F]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t('cms.selectApartment')}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function StatusDot({ status }) {
  const colors = { available: '#2E7D32', reserved: '#D4AF37', sold: '#C62828' };
  return <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[status] || '#8A828F' }} />;
}

/* Fetches image from object storage via backend proxy */
function StorageImage({ path, alt, className }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!path) return;
    let revoke = null;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(res => {
        const url = URL.createObjectURL(res.data);
        setSrc(url);
        revoke = url;
      })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);

  if (!src) return <div className={`bg-[#F5F5F7] flex items-center justify-center ${className}`}><Loader2 size={16} className="animate-spin text-[#8A828F]" /></div>;
  return <img src={src} alt={alt} className={className} />;
}

/* ============= FLOOR PLANS CMS PANEL ============= */
function FloorPlansCMSPanel({ t, lang }) {
  const [tower, setTower] = useState('kirei1');
  const [floorPlans, setFloorPlans] = useState({});
  const [uploading, setUploading] = useState(null); // floor number being uploaded
  const fileRefs = useRef({});

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/floor-plans/${tower}`);
      setFloorPlans(res.data);
    } catch {}
  }, [tower]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleUpload = async (floor, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(floor);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/floor-plans/${tower}/${floor}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchPlans();
      toast.success(lang === 'es' ? `Plano del Piso ${floor} subido` : `Floor ${floor} plan uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
      if (fileRefs.current[floor]) fileRefs.current[floor].value = '';
    }
  };

  const floors = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Tower selector */}
      <div className="bg-white border border-[#E5E5E5] p-6" data-testid="floorplans-tower-selector">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {lang === 'es' ? 'Planos de Planta por Nivel' : 'Floor Plans by Level'}
          </h3>
          <div className="flex gap-2">
            {['kirei1', 'kirei2'].map(tw => (
              <button
                key={tw}
                data-testid={`fp-tower-${tw}`}
                onClick={() => setTower(tw)}
                className={`px-5 py-2 text-xs tracking-[0.1em] uppercase border transition-colors ${
                  tower === tw
                    ? 'bg-[#D4AF37] text-white border-[#D4AF37]'
                    : 'text-[#8A828F] border-[#E5E5E5] hover:border-[#D4AF37]'
                }`}
              >
                {tw === 'kirei1' ? 'Kirei I' : 'Kirei II'}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-[#8A828F]">
          {lang === 'es'
            ? 'Sube el plano completo de cada nivel. Esta imagen se mostrará en la vista de detalle del piso.'
            : 'Upload the full floor plan for each level. This image will be shown in the floor detail view.'}
        </p>
      </div>

      {/* Floor plan grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {floors.map(floor => {
          const plan = floorPlans[floor];
          const hasImage = plan?.floorplan_image;
          const isUploading = uploading === floor;

          return (
            <div key={floor} className="bg-white border border-[#E5E5E5] overflow-hidden" data-testid={`fp-floor-${floor}`}>
              <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {floor >= 9 ? 'PH — ' : ''}{lang === 'es' ? 'Piso' : 'Floor'} {floor}
                  </span>
                  {hasImage && (
                    <span className="ml-2 text-[10px] text-[#2E7D32] tracking-[0.08em] uppercase">
                      {lang === 'es' ? 'Subido' : 'Uploaded'}
                    </span>
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    ref={el => fileRefs.current[floor] = el}
                    accept="image/*,.svg,.pdf"
                    onChange={(e) => handleUpload(floor, e)}
                    className="hidden"
                    data-testid={`fp-input-${floor}`}
                  />
                  <button
                    onClick={() => fileRefs.current[floor]?.click()}
                    disabled={isUploading}
                    data-testid={`fp-upload-btn-${floor}`}
                    className="flex items-center gap-1.5 text-xs tracking-[0.08em] uppercase border border-[#D4AF37] text-[#D4AF37] px-4 py-1.5 hover:bg-[#D4AF37] hover:text-white transition-all disabled:opacity-50"
                  >
                    {isUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    {isUploading
                      ? (lang === 'es' ? 'Subiendo...' : 'Uploading...')
                      : hasImage
                        ? (lang === 'es' ? 'Cambiar' : 'Change')
                        : (lang === 'es' ? 'Subir Plano' : 'Upload Plan')}
                  </button>
                </div>
              </div>
              {/* Preview */}
              <div className="h-40 bg-[#F5F5F7] flex items-center justify-center">
                {hasImage ? (
                  <StorageImage path={plan.floorplan_image} alt={`Floor ${floor}`} className="w-full h-40 object-contain" />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-[#C5C0C8]">
                    <Map size={24} />
                    <span className="text-[10px] tracking-[0.08em]">
                      {lang === 'es' ? 'Sin plano' : 'No plan'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
